import styled from "styled-components";

import {
  SelectPill,
  SheetOptions,
} from "../../../../../components/MobileForm/styles";
import { SPACING, FONTS, SHADOWS } from "../../../../../utils/constants";
import { AmountVariant, amountVariantColor } from "../../../../../utils/tone";
import { sectionColumnHeaderCellStyles } from "../../../../../components/SortableColumnHeader/styles";
import {
  SectionFooter,
  sectionInsetInline,
  sectionTableEdges,
} from "../SectionCard/styles";
import { ActivityTypeTone } from "./types";
import Button from "../../../../../components/Button";
import { SectionRetryButton } from "../../../../../components/SectionRetryButton";

export {
  SectionCard as ActivityFeedCard,
  SectionHeaderRow as HeaderRow,
  SectionTitleBlock as TitleBlock,
  SectionTitle as Title,
  SectionSubtitle as Subtitle,
  SectionBadge as Badge,
  SectionCardList as CardList,
  SectionCardRow as CardRow,
  SectionMessage as Message,
  SectionFooter as PagerBand,
  SectionToolbar as Toolbar,
  SectionToolbarGroup as ToolbarGroup,
  SectionSearchField as SearchField,
  SectionSearchIcon as SearchIcon,
  SectionSearchInput as SearchInput,
  SectionSearchClearButton as SearchClearButton,
  SectionNoticeInset as NoticeInset,
} from "../SectionCard/styles";

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

/* ------------------------------ Desktop table ----------------------------- */

// A real table, not a div grid: only <th scope="col">/<td> carries the figure-to-heading
// relationship to a screen reader; table-layout: fixed keeps a long name from reflowing money columns.
export const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  ${sectionTableEdges}
`;

export const HeaderCell = styled.th<{
  $align?: "center" | "end";
  $width: string;
}>`
  ${sectionColumnHeaderCellStyles}
  width: ${({ $width }) => $width};
`;

// The actions column carries no visible heading, but it is still a column: the
// label keeps it announced without widening the header visually.
export const HiddenHeaderLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

export const Row = styled.tr`
  border-bottom: 1px solid var(--color-card-border);
`;

export const Cell = styled.td<{ $align?: "center" | "end" }>`
  padding: ${SPACING.md}px ${SPACING.sm}px;
  text-align: ${({ $align }) => $align ?? "start"};
  vertical-align: top;
  color: var(--color-text-default);
  font-size: var(--text-md);
`;

// A `td` that is itself a flex container loses its cell semantics in some browsers.
export const PositionStack = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const InstrumentSymbol = styled.span`
  color: var(--color-text-default);
  font-size: var(--text-md);
  font-weight: 700;
`;

// The exchange, not the name: it is what tells two listings of the same symbol
// apart, and the column has room for one line under the symbol.
export const InstrumentExchange = styled.span`
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--text-base);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Mirrors the mockup's StatusBadge: warning is a filled pill, neutral the bordered muted one.
export const TypeBadge = styled.span<{ $tone: ActivityTypeTone }>`
  display: inline-flex;
  align-items: center;
  padding: ${SPACING.xxs}px ${SPACING.sm}px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === "warning" ? "transparent" : "var(--color-card-border)"};
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "warning"
      ? "var(--color-status-warning-background)"
      : "transparent"};
  color: ${({ $tone }) =>
    $tone === "warning"
      ? "var(--color-status-warning)"
      : "var(--color-text-secondary)"};
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const LockedChip = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-style: italic;
`;

export const DetailsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const DetailsInline = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.xs}px;
`;

export const AmountText = styled.span<{ $variant?: AmountVariant }>`
  color: ${({ $variant }) => amountVariantColor($variant)};
  font-weight: 700;
`;

export const CardAmountFigure = styled.span<{
  $variant?: AmountVariant;
}>`
  color: ${({ $variant }) => amountVariantColor($variant)};
`;

export const MutedText = styled.span`
  color: var(--color-text-muted);
`;

// Shared by command and action notes on purpose — separate treatments would read as two
// different kinds of remark in the same table. The aliases below name what each caller means.
export const FeedNote = styled.span`
  display: block;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  font-style: italic;
`;

// Why a command isn't fully correctable — names the actual owner of the correction (corporate
// action, transfer transaction, broker statement).
export const CorrectionNote = FeedNote;

// Own alias, not reused from CorrectionNote: that one explains a refusal, this one quotes the user.
export const UserNote = FeedNote;

export const KebabAlign = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/* --------------------------- Corporate actions ---------------------------- */

// What the user still owes, or the settlement date — same treatment as CorrectionNote, same kind of line.
export const ActionNote = FeedNote;

/* ------------------------------ Mobile cards ------------------------------ */

// Auto columns after the date, so a corporate action's extra state badge takes its own track.
export const CardHeading = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-columns: auto;
  grid-auto-flow: column;
  align-items: center;
  gap: ${SPACING.sm}px;
`;

export const CardDate = styled.span`
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
`;

export const CardPrimaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  margin-top: ${SPACING.xs}px;
  color: var(--color-text-default);
  font-size: var(--text-md);
  font-weight: 700;
`;

export const CardDetails = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  line-height: 1.35;
`;

export const CardQuantityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  margin-top: ${SPACING.xs}px;
  padding-top: ${SPACING.sm}px;
  border-top: 1px solid var(--color-card-border);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
`;

export const CardQuantityValue = styled.span`
  color: var(--color-text-default);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

/* --------------------------------- Footer --------------------------------- */

export const Footer = styled(SectionFooter)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACING.md}px;
`;

export const FooterText = styled.span`
  font-style: italic;
  color: var(--color-text-secondary);
  font-size: var(--text-base);
`;

export const LoadMoreLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
`;

/* --------------------------------- States --------------------------------- */

export const RetryButton = styled(SectionRetryButton)`
  margin-bottom: ${SPACING.lg}px;
`;

export const DateRangeNote = styled.p`
  margin: 0;
  padding-block: ${SPACING.xs}px ${SPACING.sm}px;
  ${sectionInsetInline()}
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
`;

export const PageNote = styled.p`
  margin: 0;
  padding-block: ${SPACING.sm}px;
  ${sectionInsetInline()}
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  font-style: italic;
`;

/* ---------------------------- Checkbox filter ----------------------------- */

export const CheckboxFilter = styled.div`
  position: relative;
`;

export const CheckboxFilterTrigger = styled(SelectPill)``;

export const CheckboxFilterPopover = styled.div`
  position: absolute;
  top: calc(100% + ${SPACING.xs}px);
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 220px;
  max-width: calc(100vw - ${SPACING.xl}px);
  max-height: min(360px, 60vh);
  overflow-y: auto;
  padding: ${SPACING.sm}px;
  border: 1px solid var(--color-card-border);
  border-radius: ${SPACING.xs}px;
  background: var(--color-card-background);
  box-shadow: ${SHADOWS.large2};
`;

export const CheckboxFilterSheetOptions = styled(SheetOptions)`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  padding: ${SPACING.sm}px ${SPACING.md}px;
`;

export const CheckboxFilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.xxs}px ${SPACING.xs}px;
  border-radius: ${SPACING.xs}px;
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-base);
  cursor: pointer;

  &:hover {
    background: var(--hover-background);
  }

  & > input {
    accent-color: var(--color-primary);
  }
`;

// Sticky so a long holding list never scrolls Clear out of reach.
export const CheckboxFilterClear = styled(Button)`
  position: sticky;
  bottom: 0;
  align-self: flex-end;
  background: var(--color-card-background);
`;
